/**
 * ItemCustomizationModal - Shared customization modal for Kiosk, POS, Server pages
 * Reusable component with full customization support matching MenuItemCard functionality
 * Supports: Sizes, Modifiers, Ingredients (remove/extra), Add-ons, Special notes
 */
import { useState, useEffect, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Check, X, Sparkles, TrendingUp, ShoppingBag, Loader2, Crown, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

// Error Boundary to prevent white screen crashes
class ModalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ItemCustomizationModal Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-orange-500 mb-4" />
          <h3 className="text-lg font-bold mb-2">Something went wrong</h3>
          <p className="text-gray-500 mb-4">Unable to load item customization</p>
          <Button 
            onClick={() => {
              this.setState({ hasError: false, error: null });
              this.props.onClose?.();
            }}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Close
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Helper: Check if an ingredient is a core/essential ingredient for this item
const isCoreIngredient = (item, ingredientName) => {
  const itemNameLower = item.name?.toLowerCase() || '';
  const itemDescLower = item.description?.toLowerCase() || '';
  const ingNameLower = ingredientName?.toLowerCase() || '';
  
  const corePatterns = [
    { itemKeywords: ['coffee', 'espresso', 'latte', 'cappuccino', 'macchiato', 'mocha', 'americano'], 
      ingredientKeywords: ['coffee', 'espresso', 'shot'] },
    { itemKeywords: ['egg', 'eggs', 'benedict', 'omelette', 'omelet', 'frittata', 'scrambled', 'poached'],
      ingredientKeywords: ['egg', 'eggs'] },
    { itemKeywords: ['chicken wrap', 'chicken sandwich', 'chicken burger', 'grilled chicken'], 
      ingredientKeywords: ['chicken'] },
    { itemKeywords: ['beef burger', 'beef wrap', 'steak'], ingredientKeywords: ['beef', 'steak'] },
    { itemKeywords: ['fish taco', 'fish wrap', 'grilled fish'], ingredientKeywords: ['fish'] },
    { itemKeywords: ['salmon'], ingredientKeywords: ['salmon'] },
    { itemKeywords: ['tuna'], ingredientKeywords: ['tuna'] },
    { itemKeywords: ['shrimp', 'prawn'], ingredientKeywords: ['shrimp', 'prawn'] },
    { itemKeywords: ['acai'], ingredientKeywords: ['acai'] },
  ];
  
  for (const pattern of corePatterns) {
    const itemNameMatches = pattern.itemKeywords.some(k => itemNameLower.includes(k));
    const itemDescMatches = pattern.itemKeywords.some(k => itemDescLower.includes(k));
    const ingMatches = pattern.ingredientKeywords.some(k => ingNameLower.includes(k));
    
    if ((itemNameMatches || itemDescMatches) && ingMatches) {
      return true;
    }
  }
  
  const blendedPatterns = [/blended with ([^,]+)/i, /made with ([^,]+)/i, /([a-z]+) base/i];
  
  for (const pattern of blendedPatterns) {
    const match = itemDescLower.match(pattern);
    if (match && match[1]) {
      const coreIngredient = match[1].trim().toLowerCase();
      if (ingNameLower.includes(coreIngredient) || coreIngredient.includes(ingNameLower)) {
        return true;
      }
    }
  }
  
  return false;
};

function ItemCustomizationModalContent({
  item,
  open,
  onOpenChange,
  onAddToCart,
  availableAddOns = [],
  theme = 'dark', // 'dark' for kiosk/pos/server, 'light' for customer-facing
  showPrices = true,
  isAccessible = false,
  kioskPortraitMode = false, // For portrait kiosk mode
  coffeePassStatus = null, // Coffee Pass status for redemption
  onCoffeePassRedeemed = null // Callback when pass is redeemed
}) {
  const [customization, setCustomization] = useState(null);
  const [loadingCustomization, setLoadingCustomization] = useState(false);
  const [popularCustomizations, setPopularCustomizations] = useState(null);
  const [redeemingPass, setRedeemingPass] = useState(false);
  const [upgradingPass, setUpgradingPass] = useState(false);
  const [passUpgraded, setPassUpgraded] = useState(false);
  
  // Customization state
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [removedIngredients, setRemovedIngredients] = useState([]);
  const [extraIngredients, setExtraIngredients] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [specialNotes, setSpecialNotes] = useState('');
  const [quantity, setQuantity] = useState(1);
  
  // Coffee Pass redemption - check if this item is eligible and pass can be used
  // PRIMARY CHECK: Use is_coffee_pass_eligible flag from database (set via Admin Menu)
  // FALLBACK: Also check category/name patterns for backwards compatibility
  const isCoffeeOrDrink = item?.is_coffee_pass_eligible === true || 
                          item?.isCoffeePassEligible === true ||
                          item?.category === 'coffee' || item?.category === 'iced-drinks' || 
                          item?.name?.toLowerCase().includes('coffee') ||
                          item?.name?.toLowerCase().includes('latte') ||
                          item?.name?.toLowerCase().includes('cappuccino') ||
                          item?.name?.toLowerCase().includes('espresso') ||
                          item?.name?.toLowerCase().includes('mocha') ||
                          item?.name?.toLowerCase().includes('matcha') ||
                          item?.name?.toLowerCase().includes('hot chocolate') ||
                          item?.name?.toLowerCase().includes('decaf');
  
  // Can redeem if: pass can_redeem_now, OR pass was just upgraded (passUpgraded flag)
  const canRedeemCoffeePass = (coffeePassStatus?.can_redeem_now || passUpgraded) && isCoffeeOrDrink;
  const canUpgradeToRedeem = coffeePassStatus?.can_upgrade_to_redeem && isCoffeeOrDrink && !passUpgraded;
  const coffeePassUpgradePrice = coffeePassStatus?.upgrade_price || 3.00;
  const coffeePassMilkPrice = coffeePassStatus?.milk_upgrade_price || 1.00;

  // Fetch customization when modal opens
  useEffect(() => {
    if (open && item && !customization) {
      fetchCustomizationOptimized();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  // Set default size when customization loads - DO NOT auto-select sizes
  // The base item.price is the default, sizes are optional upsells
  useEffect(() => {
    // Don't auto-select any size - let user choose if they want an upsell
    // The default price is item.price (what was entered when creating the item)
    if (customization || item) {
      setSelectedSize(null); // No size selected by default
    }
  }, [customization, item]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCustomization(null);
      setPopularCustomizations(null);
      setSelectedSize(null);
      setSelectedModifiers([]);
      setRemovedIngredients([]);
      setExtraIngredients([]);
      setSelectedAddOns([]);
      setSpecialNotes('');
      setQuantity(1);
      setPassUpgraded(false);
    }
  }, [open]);

  // OPTIMIZED: Single API call for both customization and popular customizations
  // Added specific timeout for better UX on Server Tablets
  const fetchCustomizationOptimized = async () => {
    if (!item?.id) return;
    setLoadingCustomization(true);
    try {
      // Use the optimized combined endpoint with 10s timeout (reasonable for poor connections)
      const res = await api.get(`/menu/${item.id}/customization-full`, { timeout: 10000 });
      const data = res.data;
      setCustomization({
        ingredients: data.customization?.ingredients || [],
        modifiers: data.customization?.modifiers || [],
        extras: data.customization?.extras || [],
        size_modifiers: data.customization?.size_modifiers || [],
        sizes: data.sizes || [],
        // Global modifier groups (e.g., Milk Type, Sugar Level)
        modifier_groups: data.modifier_groups || []
      });
      // Popular customizations are included in the same response
      if (data.popular_customizations || data.suggestion_label) {
        setPopularCustomizations({
          popular_customizations: data.popular_customizations || [],
          suggestion_label: data.suggestion_label
        });
      }
    } catch (err) {
      console.error('Failed to fetch customization:', err);
      // Show user-friendly message on timeout
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Loading customization timed out. Please try again.');
      }
      // Try fallback
      try {
        await fetchCustomizationFallback();
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
        // Allow user to add item without customization
        setCustomization({
          ingredients: [],
          modifiers: [],
          extras: [],
          size_modifiers: [],
          sizes: item?.sizes || [],
          modifier_groups: []
        });
      }
    } finally {
      setLoadingCustomization(false);
    }
  };

  // Fallback to original separate API calls
  const fetchCustomizationFallback = async () => {
    if (!item?.id) return;
    try {
      const res = await api.get(`/menu/${item.id}/customization`);
      setCustomization(res.data.customization);
    } catch (err) {
      console.error('Fallback fetch failed:', err);
    }
  };

  // Apply popular customization
  const applyPopularCustomization = (customizationItem) => {
    const { name, type, price } = customizationItem;
    
    if (type === 'extra') {
      if (!extraIngredients.find(e => e.name === name)) {
        setExtraIngredients(prev => [...prev, { name, price: price || 0 }]);
        toast.success(`Added ${name}`);
      }
    } else if (type === 'modifier') {
      if (!selectedModifiers.find(m => m.name === name)) {
        setSelectedModifiers(prev => [...prev, { name, price: price || 0 }]);
        toast.success(`Added ${name}`);
      }
    } else if (type === 'size') {
      setSelectedSize(name);
      toast.success(`Size set to ${name}`);
    }
  };

  // Calculate total price
  const calculatePrice = () => {
    let price = item?.price || 0;
    
    const sizes = customization?.sizes || item?.sizes;
    // Normalize selectedSize - handle both string and object
    const sizeName = typeof selectedSize === 'string' ? selectedSize : selectedSize?.name;
    
    if (sizeName && sizes) {
      const sizeData = sizes.find(s => s.name === sizeName);
      if (sizeData) price = Number(sizeData.price) || 0;
    }
    
    selectedModifiers.forEach(mod => {
      const sizeModifiers = customization?.size_modifiers || [];
      const sizeSpecific = sizeModifiers.find(
        sm => sm.modifier_name === (mod.category || mod.name) && sm.size === sizeName
      );
      // Priority: size-specific > price_adjustment stored on mod > price > 0
      const modPrice = sizeSpecific?.price ?? mod.price_adjustment ?? mod.price ?? 0;
      // Multiply by quantity for modifiers with quantity (like milk types)
      price += (Number(modPrice) || 0) * (mod.quantity || 1);
    });
    
    extraIngredients.forEach(ing => {
      price += Number(ing.price) || 0;
    });
    
    selectedAddOns.forEach(addon => {
      // Add-ons now have quantity - multiply price by quantity
      price += (Number(addon.price) || 0) * (addon.quantity || 1);
    });
    
    return price;
  };

  /**
   * Get the price for a modifier option.
   * Priority: size-specific price > price_adjustment > price > 0
   * 
   * @param {Object} modifier - The modifier category object
   * @returns {number} The price adjustment for this modifier
   */
  const getModifierPrice = (modifier) => {
    const sizeModifiers = customization?.size_modifiers || [];
    const sizeName = typeof selectedSize === 'string' ? selectedSize : selectedSize?.name;
    const sizeSpecific = sizeModifiers.find(
      sm => sm.modifier_name === modifier.name && sm.size === sizeName
    );
    
    // Priority: size-specific > price_adjustment > price > 0
    // Use null-safe numeric handling: valid 0 remains 0, undefined/null falls through
    if (sizeSpecific?.price !== undefined && sizeSpecific?.price !== null) {
      return Number(sizeSpecific.price);
    }
    if (modifier.price_adjustment !== undefined && modifier.price_adjustment !== null) {
      return Number(modifier.price_adjustment);
    }
    if (modifier.price !== undefined && modifier.price !== null) {
      return Number(modifier.price);
    }
    return 0;
  };

  // Toggle modifier selection - now handles categories with options
  const toggleModifier = (modifier) => {
    const priceAdjustment = getModifierPrice(modifier);
    const exists = selectedModifiers.find(m => m.name === modifier.name);
    
    if (exists) {
      setSelectedModifiers(prev => prev.filter(m => m.name !== modifier.name));
    } else {
      setSelectedModifiers(prev => [...prev, { 
        name: modifier.name, 
        price_adjustment: priceAdjustment,
        price: priceAdjustment  // backward compatibility
      }]);
    }
  };

  // Select an option within a modifier category (for modifiers with options array)
  // Now supports quantity for milk-type modifiers to match item quantity
  const selectModifierOption = (category, option, priceAdjustment = 0) => {
    setSelectedModifiers(prev => {
      // Check if this is a milk-type category (allows multiple quantities)
      const isMilkCategory = category.toLowerCase().includes('milk');
      
      if (isMilkCategory) {
        // For milk: allow multiple selections with quantity
        const existing = prev.find(m => m.category === category && m.name === option);
        if (existing) {
          // If already selected, remove it (toggle off)
          return prev.filter(m => !(m.category === category && m.name === option));
        } else {
          // Add new selection with quantity 1
          return [...prev, { 
            category: category,       // e.g., "Milk Type"
            name: option,             // e.g., "Oat Milk"
            price_adjustment: priceAdjustment,
            price: priceAdjustment,
            quantity: 1               // Start with quantity 1
          }];
        }
      } else {
        // For non-milk categories: single selection (replace previous)
        const filtered = prev.filter(m => m.category !== category);
        return [...filtered, { 
          category: category,
          name: option,
          price_adjustment: priceAdjustment,
          price: priceAdjustment,
          quantity: 1
        }];
      }
    });
  };

  // Increase modifier quantity (for milk-type modifiers)
  const increaseModifierQuantity = (category, name, maxQty) => {
    setSelectedModifiers(prev => prev.map(m => {
      if (m.category === category && m.name === name) {
        const newQty = Math.min((m.quantity || 1) + 1, maxQty || 99);
        return { ...m, quantity: newQty };
      }
      return m;
    }));
  };

  // Decrease modifier quantity (for milk-type modifiers)
  const decreaseModifierQuantity = (category, name) => {
    setSelectedModifiers(prev => {
      return prev.map(m => {
        if (m.category === category && m.name === name) {
          const newQty = (m.quantity || 1) - 1;
          if (newQty <= 0) {
            return null; // Mark for removal
          }
          return { ...m, quantity: newQty };
        }
        return m;
      }).filter(Boolean); // Remove nulls
    });
  };

  // Get modifier quantity
  const getModifierQuantity = (category, name) => {
    const mod = selectedModifiers.find(m => m.category === category && m.name === name);
    return mod?.quantity || 0;
  };

  // Check if an option is selected within a category
  const isOptionSelected = (category, option) => {
    return selectedModifiers.some(m => m.category === category && m.name === option);
  };

  // Get the selected option for a category
  const getSelectedOption = (category) => {
    const mod = selectedModifiers.find(m => m.category === category);
    return mod?.name || null;
  };

  // Toggle ingredient removal
  const toggleRemoveIngredient = (ingredientName) => {
    if (removedIngredients.includes(ingredientName)) {
      setRemovedIngredients(prev => prev.filter(i => i !== ingredientName));
    } else {
      setRemovedIngredients(prev => [...prev, ingredientName]);
    }
  };

  // Toggle extra ingredient
  const toggleExtraIngredient = (ingredient) => {
    const exists = extraIngredients.find(i => i.name === ingredient.name);
    if (exists) {
      setExtraIngredients(prev => prev.filter(i => i.name !== ingredient.name));
    } else {
      setExtraIngredients(prev => [...prev, { name: ingredient.name, price: ingredient.extra_price || 0 }]);
    }
  };

  // Syrup names for exclusive selection (only one syrup at a time, but can add multiple shots)
  const SYRUP_NAMES = ['caramel', 'hazelnut', 'vanilla', 'caramel syrup', 'hazelnut syrup', 'vanilla syrup'];
  
  const isSyrup = (addonName) => {
    const nameLower = addonName?.toLowerCase() || '';
    return SYRUP_NAMES.some(syrup => nameLower.includes(syrup));
  };
  
  // Check if a syrup is already selected
  const getSelectedSyrup = () => {
    return selectedAddOns.find(a => isSyrup(a.name));
  };
  
  // Check if an add-on should be disabled (syrup exclusivity)
  const isAddOnDisabled = (addon) => {
    if (!isSyrup(addon.name)) return false;
    const selectedSyrup = getSelectedSyrup();
    return selectedSyrup && selectedSyrup.name !== addon.name;
  };

  // Toggle add-on - now supports quantity and syrup exclusivity
  const toggleAddOn = (addon) => {
    const exists = selectedAddOns.find(a => a.name === addon.name);
    
    if (exists) {
      // Remove the add-on
      setSelectedAddOns(prev => prev.filter(a => a.name !== addon.name));
    } else {
      // For syrups: remove any existing syrup first (only one syrup allowed)
      if (isSyrup(addon.name)) {
        setSelectedAddOns(prev => {
          const withoutSyrups = prev.filter(a => !isSyrup(a.name));
          return [...withoutSyrups, { name: addon.name, price: addon.price, quantity: 1 }];
        });
      } else {
        // Regular add-on: just add with quantity 1
        setSelectedAddOns(prev => [...prev, { name: addon.name, price: addon.price, quantity: 1 }]);
      }
    }
  };

  // Increase add-on quantity
  const increaseAddOnQuantity = (addonName) => {
    setSelectedAddOns(prev => prev.map(a => 
      a.name === addonName 
        ? { ...a, quantity: (a.quantity || 1) + 1 }
        : a
    ));
  };

  // Decrease add-on quantity (removes if quantity reaches 0)
  const decreaseAddOnQuantity = (addonName) => {
    setSelectedAddOns(prev => {
      return prev.map(a => {
        if (a.name === addonName) {
          const newQty = (a.quantity || 1) - 1;
          if (newQty <= 0) return null;
          return { ...a, quantity: newQty };
        }
        return a;
      }).filter(Boolean);
    });
  };

  // Filter compatible add-ons
  const compatibleAddOns = availableAddOns.filter(addon => {
    if (!addon.compatible_categories || addon.compatible_categories.length === 0) return true;
    return addon.compatible_categories.includes(item?.category);
  });

  // Handle add to cart
  const handleAddToCart = async () => {
    // Normalize selectedSize - ensure it's stored consistently
    const normalizedSize = selectedSize 
      ? (typeof selectedSize === 'string' ? selectedSize : selectedSize.name)
      : null;
    
    // Build modifiers array with proper category, option names, and price_adjustment
    const modifiersArray = selectedModifiers.map(mod => ({
      modifier_name: mod.category || mod.name,  // e.g., "Milk Type" or legacy flat modifier name
      selected_option: mod.name,                 // e.g., "Oat Milk"
      price_adjustment: mod.price_adjustment ?? mod.price ?? 0  // Preserve the adjustment
    }));

    // Check if this is a Coffee Pass redemption
    if (canRedeemCoffeePass && coffeePassStatus?.pass_id) {
      setRedeemingPass(true);
      try {
        // Determine if size is an upgrade (12oz / Large = +$3, 8oz / Small = FREE)
        // Check multiple patterns for size upgrade detection
        const sizeLower = (normalizedSize || '').toLowerCase();
        const is12oz = sizeLower.includes('12') || 
                       sizeLower.includes('large') ||
                       sizeLower.includes('grande') ||
                       sizeLower.includes('medium') ||  // Some cafes use Medium as upgrade
                       sizeLower.includes('regular');   // "Regular" often means not-small
        
        // Also check if it's explicitly the small/8oz (FREE) option
        const isSmall = sizeLower.includes('8') || 
                        sizeLower.includes('small') ||
                        sizeLower.includes('short') ||
                        sizeLower.includes('piccolo');
        
        // If not explicitly small, and a size is selected, it's likely an upgrade
        const isUpgradeSize = normalizedSize && !isSmall;
        
        // Find milk modifier if selected
        const milkModifier = modifiersArray.find(m => 
          m.modifier_name?.toLowerCase().includes('milk') || 
          m.selected_option?.toLowerCase().includes('milk') ||
          m.modifier_name?.toLowerCase().includes('dairy') ||
          m.selected_option?.toLowerCase().includes('oat') ||
          m.selected_option?.toLowerCase().includes('almond') ||
          m.selected_option?.toLowerCase().includes('soy') ||
          m.selected_option?.toLowerCase().includes('coconut')
        );
        const milkType = milkModifier?.selected_option || 'Regular Milk';
        // Specialty milk = anything that's not "Regular", "Skim", or default - these are FREE
        // FREE milks: Regular Milk, Skim Milk, Full Cream, Whole Milk
        const milkLower = milkType.toLowerCase();
        const isFreeMilk = milkLower.includes('regular') || 
                          milkLower.includes('skim') || 
                          milkLower.includes('full cream') ||
                          milkLower.includes('whole');
        const isSpecialtyMilk = milkType && !isFreeMilk;
        
        // Calculate milk price - use the modifier's actual price if set (use ?? for nullish check, not ||)
        // This ensures $0 prices from menu are respected, not replaced with default $1
        const milkUpgradePrice = isSpecialtyMilk 
          ? (milkModifier?.price_adjustment ?? milkModifier?.price ?? coffeePassMilkPrice) 
          : 0;
        
        // Call the redemption API
        const res = await api.post('/coffee-pass/redeem', {
          pass_id: coffeePassStatus.pass_id,
          item_id: item.id,
          item_name: item.name,
          item_category: item.category || 'coffee',
          upgrade_to_12oz: is12oz,
          milk_type: milkType,
          special_instructions: specialNotes || null,
          modifiers: modifiersArray.length > 0 ? modifiersArray : null,
          channel: 'kiosk'
        });
        
        if (res.data.success) {
          // Calculate the final price (only upgrades cost money)
          let finalPrice = 0;
          // Size upgrade price - use $3 default or from settings
          if (isUpgradeSize || is12oz) finalPrice += coffeePassUpgradePrice;
          // Milk upgrade price - use the actual modifier price if available
          if (isSpecialtyMilk) finalPrice += milkUpgradePrice;
          
          // Construct the item name with Bula Pass indicator
          const itemNameWithSize = normalizedSize 
            ? `${item.name} (${normalizedSize}) - Bula Pass`
            : `${item.name} - Bula Pass`;
          
          const cartItem = {
            item_id: `coffee-pass-${Date.now()}`,
            name: itemNameWithSize,
            price: finalPrice,
            quantity: 1,
            category: item?.category || 'coffee',
            image: item?.image,
            selectedSize: normalizedSize,
            modifiers: modifiersArray.length > 0 ? modifiersArray : null,
            specialNotes: specialNotes || null,
            removedIngredients: removedIngredients.length > 0 ? removedIngredients : null,
            extraIngredients: extraIngredients.length > 0 ? extraIngredients : null,
            addOns: selectedAddOns.length > 0 ? selectedAddOns : null,
            // Mark as Coffee Pass redemption
            isCoffeePassRedemption: true,
            isCoffeeClubRedemption: true,
            passRedemptionId: res.data.redemption_id,
            skip_customization: true
          };

          onAddToCart(cartItem);
          toast.success(res.data.message || 'Free coffee redeemed!', {
            description: finalPrice > 0 ? `Upgrades: $${finalPrice.toFixed(2)}` : 'Enjoy your free coffee!',
            icon: <Check className="w-4 h-4" />
          });
          
          // Notify parent that pass was redeemed
          if (onCoffeePassRedeemed) {
            onCoffeePassRedeemed(res.data);
          }
          
          onOpenChange(false);
          return;
        } else {
          // Redemption API returned success=false - don't add item at full price
          toast.error(res.data.message || 'Redemption failed. Please try again.');
          setRedeemingPass(false);
          return; // CRITICAL: Stop here, don't fall through to add at full price
        }
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Redemption failed';
        toast.error(errorMsg);
        // Don't close modal - let them try again or choose different options
        setRedeemingPass(false);
        return;
      } finally {
        setRedeemingPass(false);
      }
    }

    // Normal flow (not a Coffee Pass redemption)
    // Generate unique cart item ID including size and price to keep different sizes separate
    const finalPrice = calculatePrice();
    const sizeKey = normalizedSize ? `-${normalizedSize.replace(/\s+/g, '-')}-${finalPrice}` : `-base-${finalPrice}`;
    const hasCustomizations = modifiersArray.length > 0 || removedIngredients.length > 0 || 
                              extraIngredients.length > 0 || selectedAddOns.length > 0 || specialNotes;
    const customKey = hasCustomizations ? `-${Date.now()}` : '';
    
    // Construct the item name - must NOT contain "undefined"
    const itemNameWithSize = normalizedSize 
      ? `${item.name} (${normalizedSize})`
      : item.name;
    
    const cartItem = {
      item_id: `${item.id}${sizeKey}${customKey}`,
      name: itemNameWithSize,
      price: finalPrice,
      quantity: quantity,
      category: item?.category || null,
      image: item?.image,
      selectedSize: normalizedSize,  // Store as string, not object
      modifiers: modifiersArray.length > 0 ? modifiersArray : null,
      specialNotes: specialNotes || null,
      removedIngredients: removedIngredients.length > 0 ? removedIngredients : null,
      extraIngredients: extraIngredients.length > 0 ? extraIngredients : null,
      addOns: selectedAddOns.length > 0 ? selectedAddOns : null
    };

    onAddToCart(cartItem);
    toast.success(`${quantity}x ${itemNameWithSize} added to order!`);
    onOpenChange(false);
  };

  // Quick add without customization - use the BASE item price (no size/upsell selected)
  const handleAddAsIs = () => {
    // Use the original item.price - NO size selected (sizes are upsells)
    const defaultPrice = item?.price || 0;
    
    // Generate unique cart item ID with base price
    const cartItemId = `${item?.id || 'item'}-base-${defaultPrice}`;

    const cartItem = {
      item_id: cartItemId,
      name: item?.name,
      price: defaultPrice,
      quantity: 1,
      category: item?.category || null,
      image: item?.image,
      selectedSize: null, // No size selected - using base item price
      modifiers: null,
      specialNotes: null,
      removedIngredients: null,
      extraIngredients: null,
      addOns: null
    };

    onAddToCart(cartItem);
    toast.success(`${item?.name} added as-is!`);
    onOpenChange(false);
  };

  // Handle Coffee Pass upgrade from standard to extended (unlocks afternoon redemption)
  const handleUpgradePass = async () => {
    if (!coffeePassStatus?.pass_id) {
      toast.error('Cannot upgrade - pass not found');
      return;
    }
    
    setUpgradingPass(true);
    try {
      const res = await api.post('/coffee-club/upgrade-to-extended', {
        pass_id: coffeePassStatus.pass_id,
        payment_method: 'cash', // Will be settled at POS checkout
        payment_reference: `POS-UPGRADE-${Date.now()}`
      });
      
      if (res.data.success) {
        toast.success('Pass Upgraded!', {
          description: 'Customer can now redeem during afternoon hours (1-3:30 PM)',
          icon: <Crown className="w-4 h-4" />,
          duration: 5000
        });
        setPassUpgraded(true);
        // Note: The $40 upgrade fee will be added to the order at checkout
        // For now, we just enable redemption - the POS handles the payment
      }
    } catch (err) {
      console.error('Upgrade failed:', err);
      toast.error(err.response?.data?.detail || 'Upgrade failed - please try again');
    } finally {
      setUpgradingPass(false);
    }
  };

  // Get data helpers
  const getIngredients = () => customization?.ingredients || item?.ingredients || [];
  const getModifiers = () => customization?.modifiers || item?.modifiers || [];
  const getSizes = () => customization?.sizes || item?.sizes || [];

  const finalPrice = calculatePrice();
  const ingredients = getIngredients();
  const modifiers = getModifiers();
  const sizes = getSizes();
  
  const hasCustomizations = sizes.length > 0 || modifiers.length > 0 || ingredients.length > 0 || compatibleAddOns.length > 0;

  // Theme-based classes
  const isDark = theme === 'dark';
  const bgClass = isDark ? 'bg-gray-900' : 'bg-card';
  const textClass = isDark ? 'text-white' : 'text-foreground';
  const mutedTextClass = isDark ? 'text-gray-400' : 'text-muted-foreground';
  const borderClass = isDark ? 'border-gray-700' : 'border-border';
  const cardBgClass = isDark ? 'bg-gray-800' : 'bg-muted/30';
  const selectedClass = isDark ? 'border-primary bg-primary/20' : 'border-primary bg-primary/10';
  const unselectedClass = isDark ? 'border-gray-700 bg-gray-800 hover:border-primary/50' : 'border-border bg-card hover:border-primary/50';

  // Accessibility sizes
  const textSizeBase = isAccessible ? 'text-lg' : 'text-sm';
  const textSizeMd = isAccessible ? 'text-xl' : 'text-base';
  const textSizeLg = isAccessible ? 'text-2xl' : 'text-lg';
  const buttonPadding = isAccessible ? 'p-5' : 'p-3';

  if (!item) return null;

  // Portrait kiosk mode styles - renders a simple overlay (content already in rotated wrapper)
  const kioskPortraitStyles = kioskPortraitMode ? {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.9)',
    zIndex: 10002
  } : {};

  // For kiosk portrait mode, we render a custom overlay instead of using Dialog
  if (kioskPortraitMode && open) {
    return (
      <div style={kioskPortraitStyles} data-testid="kiosk-item-modal-overlay">
        <div 
          className={`w-[90%] max-w-lg max-h-[90%] overflow-y-auto rounded-2xl ${bgClass} ${textClass}`}
          data-testid="item-customization-modal"
        >
          {/* Header with Image */}
          <div className="relative">
            {item.image && (
              <img 
                src={item.image} 
                alt={item.name}
                className="w-full h-40 object-cover rounded-t-2xl"
              />
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 rounded-full text-white"
              data-testid="close-customization-modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Item Info */}
          <div className={`p-4 border-b ${borderClass}`}>
            {/* Coffee Pass Upgrade Banner - Shows when standard pass holder is in afternoon window */}
            {canUpgradeToRedeem && (
              <div className="mb-3 p-3 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-white" />
                  <span className="text-white font-bold">Upgrade to Redeem Now!</span>
                </div>
                <p className="text-purple-100 text-sm">
                  Standard pass is morning-only. Pay ${coffeePassStatus.upgrade_info?.upgrade_price || 40} to unlock afternoon redemption!
                </p>
                <p className="text-purple-200 text-xs mt-1 mb-2">
                  After upgrade: 7-10 AM AND 1-3:30 PM daily
                </p>
                <Button
                  onClick={handleUpgradePass}
                  disabled={upgradingPass}
                  className="w-full bg-white text-purple-700 hover:bg-purple-50 font-bold"
                  data-testid="upgrade-pass-btn"
                >
                  {upgradingPass ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Crown className="w-4 h-4 mr-2" />
                  )}
                  Upgrade for ${coffeePassStatus.upgrade_info?.upgrade_price || 40} - Redeem Now
                </Button>
              </div>
            )}
            
            {/* Coffee Pass Banner - Shows when item is eligible for redemption */}
            {canRedeemCoffeePass && (
              <div className="mb-3 p-3 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-white" />
                  <span className="text-white font-bold">Bula Coffee Pass - Your FREE Coffee!</span>
                </div>
                <p className="text-green-100 text-sm mt-1">
                  8oz is FREE • 12oz upgrade +${coffeePassUpgradePrice.toFixed(0)} • Specialty milk +${coffeePassMilkPrice.toFixed(0)}
                </p>
              </div>
            )}
            
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className={`font-bold ${textSizeLg}`}>{item.name}</h2>
                <p className={`${mutedTextClass} ${textSizeBase} mt-1 line-clamp-2`}>{item.description}</p>
              </div>
              {showPrices && !canRedeemCoffeePass && (
                <span className={`${textSizeLg} font-bold text-primary ml-3`}>
                  ${(item.price || 0).toFixed(2)}
                </span>
              )}
              {canRedeemCoffeePass && (
                <span className={`${textSizeLg} font-bold text-green-400 ml-3`}>
                  FREE
                </span>
              )}
            </div>
            
            {/* Quick Add As-Is Button - only show when NOT using Coffee Pass */}
            {hasCustomizations && !canRedeemCoffeePass && (
              <Button
                onClick={handleAddAsIs}
                variant="outline"
                className={`w-full mt-3 ${buttonPadding} ${isDark ? 'border-gray-600 text-white hover:bg-gray-800' : ''}`}
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Add As-Is - ${((selectedSize ? sizes.find(s => s.name === selectedSize)?.price : item.price) || 0).toFixed(2)}
              </Button>
            )}
          </div>

          {/* Loading state */}
          {loadingCustomization && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className={`ml-3 ${textSizeMd}`}>Loading options...</span>
            </div>
          )}

          {/* Customization Content */}
          {!loadingCustomization && (
            <div className="p-4 space-y-4">
              {/* Size Selection */}
              {sizes.length > 0 && (
                <div>
                  <h3 className={`font-semibold ${textSizeMd} mb-2`}>
                    Size
                    {canRedeemCoffeePass && (
                      <span className="text-amber-500 text-sm font-normal ml-2">(Bula Pass pricing)</span>
                    )}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {sizes.map(size => {
                      // Normalize for comparison
                      const normalizedSelected = typeof selectedSize === 'string' ? selectedSize : selectedSize?.name;
                      const isSelected = normalizedSelected === size.name;
                      
                      // For Coffee Pass members, calculate the upgrade price instead of full price
                      let displayPrice = Number(size.price) || 0;
                      let isFreeSize = false;
                      let isUpgradeSize = false;
                      
                      if (canRedeemCoffeePass) {
                        const sizeLower = (size.name || '').toLowerCase();
                        // 8oz / Small / Short / Piccolo = FREE
                        const isSmall = sizeLower.includes('8') || 
                                        sizeLower.includes('small') ||
                                        sizeLower.includes('short') ||
                                        sizeLower.includes('piccolo');
                        // 12oz / Large / Grande / Medium / Regular = +$3 upgrade
                        const isLarge = sizeLower.includes('12') || 
                                        sizeLower.includes('large') ||
                                        sizeLower.includes('grande') ||
                                        sizeLower.includes('medium') ||
                                        sizeLower.includes('regular');
                        
                        if (isSmall) {
                          displayPrice = 0;
                          isFreeSize = true;
                        } else if (isLarge) {
                          displayPrice = coffeePassUpgradePrice; // $3 upgrade
                          isUpgradeSize = true;
                        }
                      }
                      
                      return (
                        <button
                          key={size.name}
                          onClick={() => setSelectedSize(size.name)}
                          data-testid={`size-option-${size.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`${buttonPadding} rounded-xl border-2 transition-all ${
                            isSelected ? selectedClass : unselectedClass
                          }`}
                        >
                          <span className={`${textSizeMd} font-medium`}>{size.name}</span>
                          {showPrices && (
                            <span className={`${textSizeBase} ml-2 ${
                              isFreeSize ? 'text-green-500 font-bold' : 
                              isUpgradeSize && canRedeemCoffeePass ? 'text-amber-500' : mutedTextClass
                            }`}>
                              {isFreeSize ? 'FREE' : 
                               isUpgradeSize && canRedeemCoffeePass ? `+$${displayPrice.toFixed(2)}` :
                               `$${displayPrice.toFixed(2)}`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Modifiers - Support both flat toggles and category-based options */}
              {modifiers.length > 0 && (
                <div>
                  <h3 className={`font-semibold ${textSizeMd} mb-2`}>Options</h3>
                  <div className="space-y-4">
                    {modifiers.map(mod => {
                      // Check if modifier has nested options array
                      const hasOptions = mod.options && Array.isArray(mod.options) && mod.options.length > 0;
                      const modPrice = getModifierPrice(mod);
                      // Also check price and price_adjustment directly
                      const displayPrice = modPrice || mod.price_adjustment || mod.price || 0;
                      
                      if (hasOptions) {
                        // Category with options (e.g., Milk Type -> [Oat, Almond, Soy])
                        const isMilkCategory = mod.name.toLowerCase().includes('milk');
                        const selectedOption = getSelectedOption(mod.name);
                        return (
                          <div key={mod.name} className="space-y-2">
                            <h4 className={`font-medium ${textSizeBase} ${mutedTextClass}`}>
                              {mod.name}
                              {showPrices && displayPrice > 0 && (
                                <span className="text-primary ml-2 text-sm">(+${displayPrice.toFixed(2)} for upgrade)</span>
                              )}
                              {isMilkCategory && quantity > 1 && (
                                <span className="text-xs text-primary ml-2">(Select for each of {quantity} drinks)</span>
                              )}
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {mod.options.map(option => {
                                // Handle both string options and object options with their own prices
                                const optionName = typeof option === 'string' ? option : option.name;
                                const optionOwnPrice = typeof option === 'object' ? (option.price_adjustment || option.price || 0) : null;
                                
                                const isSelected = isOptionSelected(mod.name, optionName);
                                const optionQty = getModifierQuantity(mod.name, optionName);
                                // "Regular", "Skim" options don't have a surcharge - they are FREE
                                const optLower = optionName.toLowerCase();
                                const isDefault = optLower.includes('regular') || 
                                                  optLower.includes('skim') ||
                                                  optLower.includes('full cream') ||
                                                  optLower.includes('whole milk');
                                
                                // Price: option's own price > modifier category price > 0
                                const optionPrice = isDefault ? 0 : (optionOwnPrice !== null ? optionOwnPrice : displayPrice);
                                
                                // For milk categories with quantity > 1, show +/- controls when selected
                                if (isMilkCategory && quantity > 1 && isSelected) {
                                  return (
                                    <div
                                      key={optionName}
                                      data-testid={`modifier-option-${optionName.toLowerCase().replace(/\s+/g, '-')}`}
                                      className={`${buttonPadding} rounded-xl border-2 transition-all ${selectedClass}`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className={textSizeBase}>
                                          {optionName}
                                          {showPrices && optionPrice > 0 && (
                                            <span className="text-primary ml-1 text-xs font-semibold">+${(optionPrice * optionQty).toFixed(2)}</span>
                                          )}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              decreaseModifierQuantity(mod.name, optionName);
                                            }}
                                            className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center"
                                          >
                                            <Minus className="w-4 h-4" />
                                          </button>
                                          <span className="font-bold w-6 text-center">{optionQty}</span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              increaseModifierQuantity(mod.name, optionName, quantity);
                                            }}
                                            disabled={optionQty >= quantity}
                                            className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                              optionQty >= quantity ? 'bg-muted/50 cursor-not-allowed' : 'bg-primary hover:bg-primary/90 text-white'
                                            }`}
                                          >
                                            <Plus className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return (
                                  <button
                                    key={optionName}
                                    onClick={() => {
                                      if (isSelected) {
                                        // Deselect - remove from selectedModifiers
                                        setSelectedModifiers(prev => prev.filter(m => !(m.category === mod.name && m.name === optionName)));
                                      } else {
                                        selectModifierOption(mod.name, optionName, optionPrice);
                                      }
                                    }}
                                    data-testid={`modifier-option-${optionName.toLowerCase().replace(/\s+/g, '-')}`}
                                    className={`${buttonPadding} rounded-xl border-2 text-left transition-all ${
                                      isSelected ? selectedClass : unselectedClass
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className={textSizeBase}>
                                        {optionName}
                                        {showPrices && optionPrice > 0 && (
                                          <span className="text-primary ml-1 text-xs font-semibold">+${optionPrice.toFixed(2)}</span>
                                        )}
                                      </span>
                                      {isSelected && <Check className="w-4 h-4 text-primary" />}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                            {/* Show total milk count vs item quantity */}
                            {isMilkCategory && quantity > 1 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {(() => {
                                  const totalMilkQty = selectedModifiers
                                    .filter(m => m.category === mod.name)
                                    .reduce((sum, m) => sum + (m.quantity || 1), 0);
                                  const remaining = quantity - totalMilkQty;
                                  if (remaining > 0) {
                                    return `${remaining} drink${remaining > 1 ? 's' : ''} will use regular milk`;
                                  }
                                  return 'All drinks have milk selected';
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        // Flat modifier (toggle style) - backward compatibility
                        const isSelected = selectedModifiers.some(m => m.name === mod.name);
                        return (
                          <button
                            key={mod.name}
                            onClick={() => toggleModifier(mod)}
                            className={`w-full ${buttonPadding} rounded-xl border-2 flex items-center justify-between transition-all ${
                              isSelected ? selectedClass : unselectedClass
                            }`}
                          >
                            <span className={textSizeMd}>{mod.name}</span>
                            <div className="flex items-center gap-2">
                              {showPrices && modPrice > 0 && (
                                <span className={`${textSizeBase} text-primary`}>+${modPrice.toFixed(2)}</span>
                              )}
                              {isSelected && <Check className="w-5 h-5 text-primary" />}
                            </div>
                          </button>
                        );
                      }
                    })}
                  </div>
                </div>
              )}

              {/* ============== GLOBAL MODIFIER GROUPS ============== */}
              {/* These come from admin settings (e.g., Milk Type, Sugar Level) */}
              {customization?.modifier_groups && customization.modifier_groups.length > 0 && (
                <div className="space-y-4">
                  <h3 className={`font-semibold ${textSizeMd}`}>Customize Your Order</h3>
                  {customization.modifier_groups.map(group => {
                    const selectedOption = selectedModifiers.find(m => m.category === group.name);
                    
                    return (
                      <div key={group.id || group.name} className="space-y-2">
                        <h4 className={`font-medium ${textSizeBase} ${mutedTextClass} flex items-center gap-2`}>
                          {group.name}
                          {group.is_required && (
                            <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full">
                              Required
                            </span>
                          )}
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {(group.options || []).map(option => {
                            const optionName = option.name;
                            const optionPrice = option.price || 0;
                            const isSelected = selectedOption?.name === optionName;
                            const isDefault = optionName.toLowerCase().includes('regular') || 
                                              optionName.toLowerCase().includes('full cream') ||
                                              optionName.toLowerCase().includes('whole') ||
                                              optionName.toLowerCase().includes('normal') ||
                                              optionPrice === 0;
                            
                            return (
                              <button
                                key={optionName}
                                onClick={() => {
                                  if (isSelected) {
                                    // Deselect (unless required)
                                    if (!group.is_required) {
                                      setSelectedModifiers(prev => 
                                        prev.filter(m => m.category !== group.name)
                                      );
                                    }
                                  } else {
                                    // Select this option (replace any existing selection in this group)
                                    setSelectedModifiers(prev => {
                                      const filtered = prev.filter(m => m.category !== group.name);
                                      return [...filtered, {
                                        name: optionName,
                                        category: group.name,
                                        modifier_name: group.name,
                                        selected_option: optionName,
                                        price: optionPrice,
                                        price_adjustment: optionPrice
                                      }];
                                    });
                                  }
                                }}
                                data-testid={`modifier-group-${group.name.toLowerCase().replace(/\s+/g, '-')}-${optionName.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`${buttonPadding} rounded-xl border-2 text-left transition-all ${
                                  isSelected ? selectedClass : unselectedClass
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className={textSizeBase}>
                                    {optionName}
                                    {showPrices && optionPrice > 0 && (
                                      <span className="text-primary ml-1 text-xs font-semibold">+${optionPrice.toFixed(2)}</span>
                                    )}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {isDefault && !isSelected && (
                                      <span className="text-[10px] text-muted-foreground">FREE</span>
                                    )}
                                    {isSelected && <Check className="w-4 h-4 text-primary" />}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ============== ADD-ONS SECTION (KIOSK) ============== */}
              {/* Global add-ons like Extra Shot, Specialty Milks, Syrups */}
              {compatibleAddOns.length > 0 && (
                <div>
                  <h3 className={`font-semibold ${textSizeMd} mb-3`}>Add Extra</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {compatibleAddOns.map(addon => {
                      const selected = selectedAddOns.find(a => a.name === addon.name);
                      const isSelected = !!selected;
                      const addonQuantity = selected?.quantity || 0;
                      const isAddonSyrup = isSyrup(addon.name);
                      const disabled = isAddOnDisabled(addon);
                      
                      return (
                        <div
                          key={addon.id || addon.name}
                          data-testid={`addon-${addon.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`${buttonPadding} rounded-xl transition-all border-2 ${
                            isSelected 
                              ? selectedClass 
                              : disabled 
                                ? 'opacity-50 cursor-not-allowed border-gray-600 bg-gray-800/50' 
                                : unselectedClass
                          }`}
                        >
                          {!isSelected ? (
                            <button
                              onClick={() => !disabled && toggleAddOn(addon)}
                              disabled={disabled}
                              className="w-full flex justify-between items-center"
                            >
                              <span className={`font-medium ${textSizeMd}`}>
                                {addon.name}
                                {isAddonSyrup && <span className={`text-xs ${mutedTextClass} ml-1`}>(syrup)</span>}
                              </span>
                              {showPrices && (
                                <div className="flex items-center gap-2">
                                  <span className={`text-primary ${textSizeMd} font-medium`}>+${addon.price.toFixed(2)}</span>
                                  <div className={`w-8 h-8 rounded-full ${isDark ? 'bg-primary/30' : 'bg-primary/20'} flex items-center justify-center`}>
                                    <span className="text-primary text-xl font-bold">+</span>
                                  </div>
                                </div>
                              )}
                            </button>
                          ) : (
                            <div className="flex justify-between items-center w-full">
                              <div className="flex-1">
                                <span className={`font-medium ${textSizeMd} block`}>{addon.name}</span>
                                {showPrices && (
                                  <span className={`text-primary ${textSizeBase} font-medium`}>
                                    ${(addon.price * addonQuantity).toFixed(2)} ({addonQuantity}x ${addon.price.toFixed(2)})
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => decreaseAddOnQuantity(addon.name)}
                                  className={`w-10 h-10 rounded-full ${isDark ? 'bg-red-900/50 hover:bg-red-900' : 'bg-red-100 hover:bg-red-200'} flex items-center justify-center transition-colors`}
                                >
                                  {addonQuantity === 1 ? (
                                    <span className={`${isDark ? 'text-red-400' : 'text-red-600'} text-xl font-bold`}>×</span>
                                  ) : (
                                    <span className={`${isDark ? 'text-red-400' : 'text-red-600'} text-xl font-bold`}>−</span>
                                  )}
                                </button>
                                <span className={`w-10 text-center font-bold ${textSizeLg}`}>{addonQuantity}</span>
                                <button
                                  onClick={() => increaseAddOnQuantity(addon.name)}
                                  className={`w-10 h-10 rounded-full ${isDark ? 'bg-green-900/50 hover:bg-green-900' : 'bg-green-100 hover:bg-green-200'} flex items-center justify-center transition-colors`}
                                >
                                  <span className={`${isDark ? 'text-green-400' : 'text-green-600'} text-xl font-bold`}>+</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              {/* Ingredients */}
              {ingredients.length > 0 && (
                <div>
                  <h3 className={`font-semibold ${textSizeMd} mb-2`}>Ingredients</h3>
                  <div className="space-y-2">
                    {ingredients.map(ing => {
                      const isCore = isCoreIngredient(item, ing.name) || ing.removable === false;
                      const isRemoved = removedIngredients.includes(ing.name);
                      const hasExtra = extraIngredients.some(e => e.name === ing.name);
                      
                      return (
                        <div key={ing.name} className={`${buttonPadding} rounded-xl border ${borderClass} ${cardBgClass}`}>
                          <div className="flex items-center justify-between">
                            <span className={`${textSizeMd} ${isRemoved ? 'line-through text-gray-500' : ''}`}>
                              {ing.name}
                            </span>
                            <div className="flex gap-2">
                              {!isCore && (
                                <button
                                  onClick={() => toggleRemoveIngredient(ing.name)}
                                  className={`px-3 py-1 rounded-lg text-sm transition-all ${
                                    isRemoved 
                                      ? 'bg-red-500 text-white' 
                                      : 'bg-gray-700 text-gray-300 hover:bg-red-500/20'
                                  }`}
                                >
                                  {isRemoved ? 'Removed' : 'Remove'}
                                </button>
                              )}
                              {ing.extra_price > 0 && (
                                <button
                                  onClick={() => toggleExtraIngredient(ing)}
                                  className={`px-3 py-1 rounded-lg text-sm transition-all ${
                                    hasExtra 
                                      ? 'bg-primary text-white' 
                                      : 'bg-gray-700 text-gray-300 hover:bg-primary/20'
                                  }`}
                                >
                                  Extra +${ing.extra_price.toFixed(2)}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Special Notes */}
              <div>
                <h3 className={`font-semibold ${textSizeMd} mb-2`}>Special Instructions</h3>
                <Textarea
                  value={specialNotes}
                  onChange={(e) => setSpecialNotes(e.target.value)}
                  placeholder="Any special requests..."
                  className={`${isDark ? 'bg-gray-800 border-gray-700 text-white' : ''} ${textSizeBase}`}
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className={`p-4 border-t ${borderClass} sticky bottom-0 ${bgClass}`}>
            <Button
              onClick={handleAddToCart}
              className={`w-full ${isAccessible ? 'py-6 text-xl' : 'py-4 text-lg'} bg-primary hover:bg-primary/90`}
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Add to Cart
              {showPrices && <span className="ml-2 font-bold">${finalPrice.toFixed(2)}</span>}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={`max-w-2xl max-h-[90vh] overflow-y-auto ${bgClass} ${textClass} border-0`}
        data-testid="item-customization-modal"
        aria-describedby="customization-modal-description"
      >
        <DialogTitle className="sr-only">Customize {item.name}</DialogTitle>
        <p id="customization-modal-description" className="sr-only">
          Customize your order by selecting size, modifiers, and ingredients
        </p>
        
        {/* Header with Image */}
        <div className="relative">
          {item.image && (
            <img 
              src={item.image} 
              alt={item.name}
              className="w-full h-48 object-cover rounded-t-lg"
            />
          )}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-black/80 rounded-full text-white"
            data-testid="close-customization-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Item Info */}
        <div className={`p-4 border-b ${borderClass}`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className={`font-bold ${textSizeLg}`}>{item.name}</h2>
              <p className={`${mutedTextClass} ${textSizeBase} mt-1`}>{item.description}</p>
            </div>
            {showPrices && (
              <span className={`${textSizeLg} font-bold text-primary`}>
                ${(item.price || 0).toFixed(2)}
              </span>
            )}
          </div>
          
          {/* Quick Add As-Is Button */}
          {hasCustomizations && (
            <Button
              onClick={handleAddAsIs}
              variant="outline"
              className={`mt-4 w-full rounded-full ${isAccessible ? 'h-14 text-lg' : 'h-12'}`}
              data-testid="add-as-is-modal-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add As-Is (No customization)
            </Button>
          )}
        </div>

        {/* Customization Content */}
        <div className="p-4 space-y-6">
          {/* Add-Ons Section - Renders immediately from availableAddOns prop */}
          {compatibleAddOns.length > 0 && (
            <div>
              <h4 className={`font-semibold ${textSizeMd} mb-3`}>Add Extra</h4>
              <div className="grid grid-cols-2 gap-3">
                {compatibleAddOns.map(addon => {
                  const selected = selectedAddOns.find(a => a.name === addon.name);
                  const isSelected = !!selected;
                  const addonQuantity = selected?.quantity || 0;
                  const isAddonSyrup = isSyrup(addon.name);
                  const disabled = isAddOnDisabled(addon);
                  
                  return (
                    <div
                      key={addon.id || addon.name}
                      data-testid={`addon-${addon.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`${buttonPadding} rounded-xl transition-all border-2 ${
                        isSelected 
                          ? selectedClass 
                          : disabled 
                            ? 'opacity-50 cursor-not-allowed border-gray-600 bg-gray-800/50' 
                            : unselectedClass
                      }`}
                    >
                      {!isSelected ? (
                        // Not selected - show add button
                        <button
                          onClick={() => !disabled && toggleAddOn(addon)}
                          disabled={disabled}
                          className="w-full flex justify-between items-center"
                        >
                          <span className={`font-medium ${textSizeMd}`}>
                            {addon.name}
                            {isAddonSyrup && <span className={`text-xs ${mutedTextClass} ml-1`}>(syrup)</span>}
                          </span>
                          {showPrices && (
                            <div className="flex items-center gap-2">
                              <span className={`text-primary ${textSizeMd} font-medium`}>+${addon.price.toFixed(2)}</span>
                              <div className={`w-8 h-8 rounded-full ${isDark ? 'bg-primary/30' : 'bg-primary/20'} flex items-center justify-center`}>
                                <span className="text-primary text-xl font-bold">+</span>
                              </div>
                            </div>
                          )}
                        </button>
                      ) : (
                        // Selected - show quantity controls
                        <div className="flex justify-between items-center w-full">
                          <div className="flex-1">
                            <span className={`font-medium ${textSizeMd} block`}>{addon.name}</span>
                            {showPrices && (
                              <span className={`text-primary ${textSizeBase} font-medium`}>
                                ${(addon.price * addonQuantity).toFixed(2)} ({addonQuantity}x ${addon.price.toFixed(2)})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Decrease / Remove button */}
                            <button
                              onClick={() => decreaseAddOnQuantity(addon.name)}
                              className={`w-10 h-10 rounded-full ${isDark ? 'bg-red-900/50 hover:bg-red-900' : 'bg-red-100 hover:bg-red-200'} flex items-center justify-center transition-colors`}
                              data-testid={`addon-decrease-${addon.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {addonQuantity === 1 ? (
                                <span className={`${isDark ? 'text-red-400' : 'text-red-600'} text-xl font-bold`}>×</span>
                              ) : (
                                <span className={`${isDark ? 'text-red-400' : 'text-red-600'} text-xl font-bold`}>−</span>
                              )}
                            </button>
                            
                            {/* Quantity display */}
                            <span className={`w-10 text-center font-bold ${textSizeLg}`}>{addonQuantity}</span>
                            
                            {/* Increase button */}
                            <button
                              onClick={() => increaseAddOnQuantity(addon.name)}
                              className={`w-10 h-10 rounded-full ${isDark ? 'bg-green-900/50 hover:bg-green-900' : 'bg-green-100 hover:bg-green-200'} flex items-center justify-center transition-colors`}
                              data-testid={`addon-increase-${addon.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <span className={`${isDark ? 'text-green-400' : 'text-green-600'} text-xl font-bold`}>+</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Syrup exclusivity note */}
              {getSelectedSyrup() && (
                <p className={`text-xs ${mutedTextClass} mt-2 italic`}>
                  Only one syrup flavor can be selected. You can add multiple shots of {getSelectedSyrup().name}.
                </p>
              )}
            </div>
          )}

          {loadingCustomization ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className={`ml-2 ${textSizeBase} ${mutedTextClass}`}>Loading more options...</span>
            </div>
          ) : (
            <>
              {/* Popular Customizations */}
              {popularCustomizations?.suggestion_label && popularCustomizations.popular_customizations?.length > 0 && (
                <div className={`${isDark ? 'bg-amber-900/30 border-amber-700' : 'bg-amber-50 border-amber-200'} rounded-xl p-4 border`}>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                    <span className={`${textSizeMd} font-medium ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                      {popularCustomizations.suggestion_label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {popularCustomizations.popular_customizations.slice(0, 4).map((pop) => (
                      <button
                        key={pop.name || pop.id}
                        onClick={() => applyPopularCustomization(pop)}
                        className={`inline-flex items-center gap-1 px-4 py-2 ${isDark ? 'bg-gray-800 border-amber-700 text-amber-300 hover:bg-amber-900/50' : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-100'} rounded-full border ${textSizeBase} font-medium transition-colors`}
                        data-testid={`popular-${pop.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Sparkles className="w-4 h-4" />
                        {pop.name}
                        {showPrices && pop.price > 0 && <span className="text-amber-500">+${pop.price.toFixed(2)}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size Selection */}
              {sizes.length > 0 && (
                <div>
                  <h4 className={`font-semibold ${textSizeMd} mb-3 flex items-center gap-2`}>
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">1</span>
                    Select Size
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {sizes.map(size => {
                      // Normalize for comparison
                      const normalizedSelected = typeof selectedSize === 'string' ? selectedSize : selectedSize?.name;
                      const isSelected = normalizedSelected === size.name;
                      return (
                        <button
                          key={size.name}
                          onClick={() => setSelectedSize(size.name)}
                          data-testid={`size-${size.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`${buttonPadding} rounded-xl border-2 transition-all flex justify-between items-center ${
                            isSelected ? selectedClass : unselectedClass
                          }`}
                        >
                          <span className={`font-medium ${textSizeMd}`}>{size.name}</span>
                          {showPrices && (
                            <span className={`text-primary font-medium ${textSizeMd}`}>${(Number(size.price) || 0).toFixed(2)}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Modifiers - Support both flat toggles and category-based options */}
              {modifiers.length > 0 && (
                <div>
                  <h4 className={`font-semibold ${textSizeMd} mb-3 flex items-center gap-2`}>
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">
                      {sizes.length > 0 ? '2' : '1'}
                    </span>
                    Options
                  </h4>
                  <div className="space-y-4">
                    {modifiers.map(modifier => {
                      // Check if modifier has nested options array
                      const hasOptions = modifier.options && Array.isArray(modifier.options) && modifier.options.length > 0;
                      const modPrice = getModifierPrice(modifier);
                      
                      if (hasOptions) {
                        // Category with options (e.g., Milk Type -> [Oat, Almond, Soy])
                        const isMilkCategory = modifier.name.toLowerCase().includes('milk');
                        const selectedOption = getSelectedOption(modifier.name);
                        
                        return (
                          <div key={modifier.name} className="space-y-2">
                            <h5 className={`font-medium ${textSizeBase} ${mutedTextClass}`}>
                              {modifier.name}
                              {isMilkCategory && quantity > 1 && (
                                <span className="text-xs text-primary ml-2">(Select milk for each of {quantity} drinks)</span>
                              )}
                            </h5>
                            <div className="grid grid-cols-2 gap-2">
                              {modifier.options.map(option => {
                                const isSelected = isOptionSelected(modifier.name, option);
                                const isDefault = option.toLowerCase().includes('regular');
                                const optionPrice = isDefault ? 0 : modPrice;
                                const optionQty = getModifierQuantity(modifier.name, option);
                                
                                // For milk categories with quantity > 1, show +/- controls when selected
                                if (isMilkCategory && quantity > 1 && isSelected) {
                                  return (
                                    <div
                                      key={option}
                                      data-testid={`modifier-option-${option.toLowerCase().replace(/\s+/g, '-')}`}
                                      className={`${buttonPadding} rounded-xl border-2 transition-all ${selectedClass}`}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <span className={textSizeBase}>
                                          {option}
                                          {showPrices && optionPrice > 0 && (
                                            <span className="text-primary ml-1 text-xs">+${(optionPrice * optionQty).toFixed(2)}</span>
                                          )}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              decreaseModifierQuantity(modifier.name, option);
                                            }}
                                            className="w-7 h-7 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center"
                                            data-testid={`modifier-${option.toLowerCase().replace(/\s+/g, '-')}-decrease`}
                                          >
                                            <Minus className="w-4 h-4" />
                                          </button>
                                          <span className="font-bold w-6 text-center">{optionQty}</span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              increaseModifierQuantity(modifier.name, option, quantity);
                                            }}
                                            disabled={optionQty >= quantity}
                                            className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                              optionQty >= quantity ? 'bg-muted/50 cursor-not-allowed' : 'bg-primary hover:bg-primary/90 text-white'
                                            }`}
                                            data-testid={`modifier-${option.toLowerCase().replace(/\s+/g, '-')}-increase`}
                                          >
                                            <Plus className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return (
                                  <button
                                    key={option}
                                    onClick={() => {
                                      if (isSelected) {
                                        setSelectedModifiers(prev => prev.filter(m => !(m.category === modifier.name && m.name === option)));
                                      } else {
                                        selectModifierOption(modifier.name, option, optionPrice);
                                      }
                                    }}
                                    data-testid={`modifier-option-${option.toLowerCase().replace(/\s+/g, '-')}`}
                                    className={`${buttonPadding} rounded-xl border-2 text-left transition-all ${
                                      isSelected ? selectedClass : unselectedClass
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span className={textSizeBase}>
                                        {option}
                                        {showPrices && optionPrice > 0 && (
                                          <span className="text-primary ml-1 text-xs">+${optionPrice.toFixed(2)}</span>
                                        )}
                                      </span>
                                      {isSelected && <Check className="w-4 h-4 text-primary" />}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                            {/* Show total milk count vs item quantity */}
                            {isMilkCategory && quantity > 1 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {(() => {
                                  const totalMilkQty = selectedModifiers
                                    .filter(m => m.category === modifier.name)
                                    .reduce((sum, m) => sum + (m.quantity || 1), 0);
                                  const remaining = quantity - totalMilkQty;
                                  if (remaining > 0) {
                                    return `${remaining} drink${remaining > 1 ? 's' : ''} will use regular milk`;
                                  }
                                  return 'All drinks have milk selected';
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        // Flat modifier (toggle style) - backward compatibility
                        const isSelected = selectedModifiers.find(m => m.name === modifier.name);
                        return (
                          <button
                            key={modifier.name}
                            onClick={() => toggleModifier(modifier)}
                            data-testid={`modifier-${modifier.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`w-full ${buttonPadding} rounded-xl border-2 transition-all flex items-center justify-between ${
                              isSelected ? selectedClass : unselectedClass
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? 'border-primary bg-primary' : isDark ? 'border-gray-500' : 'border-muted-foreground'
                              }`}>
                                {isSelected && <Check className="w-4 h-4 text-white" />}
                              </div>
                              <span className={`font-medium ${textSizeMd}`}>{modifier.name}</span>
                            </div>
                            {showPrices && modPrice > 0 && (
                              <span className={`text-primary font-medium ${textSizeMd}`}>+${modPrice.toFixed(2)}</span>
                            )}
                          </button>
                        );
                      }
                    })}
                  </div>
                </div>
              )}

              {/* ============== GLOBAL MODIFIER GROUPS ============== */}
              {/* These come from admin settings (e.g., Milk Type, Sugar Level) */}
              {customization?.modifier_groups && customization.modifier_groups.length > 0 && (
                <div className="space-y-4">
                  <h4 className={`font-semibold ${textSizeMd} mb-3 flex items-center gap-2`}>
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">
                      {sizes.length > 0 && modifiers.length > 0 ? '3' : sizes.length > 0 || modifiers.length > 0 ? '2' : '1'}
                    </span>
                    Customize Your Order
                  </h4>
                  {customization.modifier_groups.map(group => {
                    const selectedOption = selectedModifiers.find(m => m.category === group.name);
                    
                    return (
                      <div key={group.id || group.name} className="space-y-2">
                        <h5 className={`font-medium ${textSizeBase} ${mutedTextClass} flex items-center gap-2`}>
                          {group.name}
                          {group.is_required && (
                            <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full">
                              Required
                            </span>
                          )}
                        </h5>
                        <div className="grid grid-cols-2 gap-2">
                          {(group.options || []).map(option => {
                            const optionName = option.name;
                            const optionPrice = option.price || 0;
                            const isSelected = selectedOption?.name === optionName;
                            const isDefault = optionName.toLowerCase().includes('regular') || 
                                              optionName.toLowerCase().includes('full cream') ||
                                              optionName.toLowerCase().includes('whole') ||
                                              optionName.toLowerCase().includes('normal') ||
                                              optionPrice === 0;
                            
                            return (
                              <button
                                key={optionName}
                                onClick={() => {
                                  if (isSelected) {
                                    // Deselect (unless required)
                                    if (!group.is_required) {
                                      setSelectedModifiers(prev => 
                                        prev.filter(m => m.category !== group.name)
                                      );
                                    }
                                  } else {
                                    // Select this option (replace any existing selection in this group)
                                    setSelectedModifiers(prev => {
                                      const filtered = prev.filter(m => m.category !== group.name);
                                      return [...filtered, {
                                        name: optionName,
                                        category: group.name,
                                        modifier_name: group.name,
                                        selected_option: optionName,
                                        price: optionPrice,
                                        price_adjustment: optionPrice
                                      }];
                                    });
                                  }
                                }}
                                data-testid={`dialog-modifier-group-${group.name.toLowerCase().replace(/\s+/g, '-')}-${optionName.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`${buttonPadding} rounded-xl border-2 text-left transition-all ${
                                  isSelected ? selectedClass : unselectedClass
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className={textSizeBase}>
                                    {optionName}
                                    {showPrices && optionPrice > 0 && (
                                      <span className="text-primary ml-1 text-xs font-semibold">+${optionPrice.toFixed(2)}</span>
                                    )}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {isDefault && !isSelected && (
                                      <span className="text-[10px] text-muted-foreground">FREE</span>
                                    )}
                                    {isSelected && <Check className="w-4 h-4 text-primary" />}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Ingredients */}
              {ingredients.length > 0 && (
                <div>
                  <h4 className={`font-semibold ${textSizeMd} mb-2 flex items-center gap-2`}>
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">
                      {sizes.length > 0 && modifiers.length > 0 ? '3' : sizes.length > 0 || modifiers.length > 0 ? '2' : '1'}
                    </span>
                    Ingredients
                  </h4>
                  <p className={`${mutedTextClass} ${textSizeBase} mb-3`}>
                    Tap remove to exclude from your order.
                  </p>
                  <div className="space-y-2">
                    {ingredients.map(ing => {
                      const isRemoved = removedIngredients.includes(ing.name);
                      const hasExtra = extraIngredients.find(e => e.name === ing.name);
                      const canRemove = ing.removable !== false && !isCoreIngredient(item, ing.name);
                      return (
                        <div 
                          key={ing.name}
                          className={`flex items-center justify-between ${buttonPadding} ${cardBgClass} rounded-xl border ${borderClass}`}
                        >
                          <span className={`font-medium ${textSizeMd} ${isRemoved ? 'line-through opacity-50' : ''}`}>
                            {ing.name}
                            {!canRemove && (
                              <span className={`ml-2 ${textSizeBase} ${mutedTextClass}`}>(essential)</span>
                            )}
                          </span>
                          <div className="flex gap-2">
                            {canRemove && (
                              <button
                                onClick={() => toggleRemoveIngredient(ing.name)}
                                data-testid={`remove-${ing.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`px-4 py-2 rounded-full ${textSizeBase} font-medium transition-all ${
                                  isRemoved
                                    ? 'bg-red-500 text-white'
                                    : isDark ? 'bg-red-900/50 text-red-400 hover:bg-red-900' : 'bg-red-100 text-red-600 hover:bg-red-200'
                                }`}
                              >
                                {isRemoved ? 'Removed' : 'Remove'}
                              </button>
                            )}
                            {(ing.extra_price > 0 || ing.price > 0) && showPrices && (
                              <button
                                onClick={() => toggleExtraIngredient(ing)}
                                data-testid={`extra-${ing.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`px-4 py-2 rounded-full ${textSizeBase} font-medium transition-all ${
                                  hasExtra
                                    ? 'bg-green-500 text-white'
                                    : isDark ? 'bg-green-900/50 text-green-400 hover:bg-green-900' : 'bg-green-100 text-green-600 hover:bg-green-200'
                                }`}
                              >
                                Extra +${(ing.extra_price || ing.price).toFixed(2)}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              <div>
                <h4 className={`font-semibold ${textSizeMd} mb-3`}>Special Instructions (optional)</h4>
                <Textarea
                  value={specialNotes}
                  onChange={(e) => setSpecialNotes(e.target.value)}
                  placeholder="Any allergies or special requests..."
                  className={`rounded-xl ${textSizeMd} min-h-[80px] ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500' : ''}`}
                  rows={2}
                  data-testid="special-notes-input"
                />
              </div>

              {/* Quantity & Add to Cart */}
              <div className={`pt-4 border-t ${borderClass}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className={`${isAccessible ? 'w-14 h-14' : 'w-12 h-12'} rounded-full ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-muted hover:bg-muted/80'} flex items-center justify-center transition-colors`}
                      data-testid="decrease-quantity"
                    >
                      <Minus className={isAccessible ? 'w-6 h-6' : 'w-5 h-5'} />
                    </button>
                    <span className={`${isAccessible ? 'w-12 text-3xl' : 'w-8 text-xl'} text-center font-bold`}>{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className={`${isAccessible ? 'w-14 h-14' : 'w-12 h-12'} rounded-full ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-muted hover:bg-muted/80'} flex items-center justify-center transition-colors`}
                      data-testid="increase-quantity"
                    >
                      <Plus className={isAccessible ? 'w-6 h-6' : 'w-5 h-5'} />
                    </button>
                  </div>
                  {showPrices && (
                    <span className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-bold text-primary`}>
                      ${(finalPrice * quantity).toFixed(2)}
                    </span>
                  )}
                </div>
                
                <Button
                  onClick={handleAddToCart}
                  className={`w-full rounded-full bg-primary hover:bg-primary/90 ${isAccessible ? 'h-16 text-xl' : 'h-14 text-lg'} font-semibold`}
                  data-testid="confirm-add-to-cart-modal"
                >
                  <ShoppingBag className={`${isAccessible ? 'w-6 h-6' : 'w-5 h-5'} mr-2`} />
                  Add {quantity} to Order {showPrices && `• $${(finalPrice * quantity).toFixed(2)}`}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Wrapper with Error Boundary to prevent white screen crashes
export default function ItemCustomizationModal(props) {
  // Early guard: don't render if modal isn't open or item is missing
  if (!props.open || !props.item) return null;
  
  return (
    <ModalErrorBoundary onClose={() => props.onOpenChange?.(false)}>
      <ItemCustomizationModalContent {...props} />
    </ModalErrorBoundary>
  );
}
